/**
 * Gravity Well — Plan 4b Task 2.1
 *
 * Radial AoE item. Steals 18-27 keys from each player in a 2-tile graph range and has
 * a 50% per-victim chance to steal one item. Stone Skin blocks the theft.
 *
 * Self-registers via addItem(gravityWell) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import type { BoardGraph } from '@shared/board/graph';
import { nodesWithinGraphRange } from '@shared/board/targeting';
import { appendEvent } from '../event-log';
import { modifyKeys } from '../economy';
import { addItem } from './registry';
import { mulberry32 } from '../dice';
import { tryBlockWithStoneSkin } from '../stone-skin-block';
import { grantPlayerItem } from '../inventory';

/** Maximum landable graph-tile range for Gravity Well to reach a victim. */
export const GRAVITY_WELL_RANGE = 2;

/** Minimum keys stolen per victim (inclusive). */
export const GRAVITY_WELL_MIN_STEAL = 18;

/** Maximum keys stolen per victim (inclusive). */
export const GRAVITY_WELL_MAX_STEAL = 27;

/** Per-victim chance to steal one item when the victim has any items. */
export const GRAVITY_WELL_ITEM_STEAL_CHANCE = 0.5;

export const gravityWell: Item = {
  id: 'gravity_well',
  name: 'Gravity Well',
  description:
    'Radial AoE (2-tile graph range) that steals 18-27 keys from each player inside and has a ' +
    '50% chance to steal one item from each victim. Blocked by Stone Skin.',
  targetType: 'none',
  category: 'complex',
  rarity: 'rare',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const graph = ctx.graph as BoardGraph | undefined;

    // Step 1: Validate caster
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Gravity Well: caster not found: ${casterId}`);
    if (!graph) throw new Error('Gravity Well: graph required for graph-range targeting');

    // Step 2: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'gravity_well',
    });

    // Step 3: Determine victims in graph range.
    const victimsInRange: string[] = [];
    const targetNodeIds = nodesWithinGraphRange(graph, caster.nodeId, GRAVITY_WELL_RANGE);
    for (const playerId of state.turnOrder) {
      if (playerId === casterId) continue;
      const player = state.players.get(playerId);
      if (!player || player.isDead) continue;
      if (targetNodeIds.has(player.nodeId)) {
        victimsInRange.push(playerId);
      }
    }

    // Step 4: Process each victim in turnOrder order
    for (const victimId of victimsInRange) {
      const victim = state.players.get(victimId)!;

      if (
        tryBlockWithStoneSkin(state, victimId, {
          actorId: casterId,
          sourceId: 'gravity_well',
        })
      ) {
        continue;
      }

      // Roll amount: 18-27 inclusive using seeded RNG
      const r = mulberry32(state.rngState);
      state.rngState = r.nextState;
      const range = GRAVITY_WELL_MAX_STEAL - GRAVITY_WELL_MIN_STEAL + 1;
      const rawAmount = Math.floor(r.value * range) + GRAVITY_WELL_MIN_STEAL;
      // Cap at victim's actual keys (no negative)
      const requestedAmount = Math.min(rawAmount, victim.keys);

      // Transfer keys
      const amount = Math.abs(modifyKeys(state, victimId, -requestedAmount, 'item_gravity_well'));
      if (amount > 0) modifyKeys(state, casterId, +amount, 'item_gravity_well');

      // Emit item_steal_keys
      appendEvent(state, {
        type: 'item_steal_keys',
        playerId: casterId,
        casterId,
        itemId: 'gravity_well',
        victimId,
        amount,
      });

      let stolenItemId: string | null = null;
      if (victim.items.length > 0) {
        const chanceRoll = mulberry32(state.rngState);
        state.rngState = chanceRoll.nextState;
        if (chanceRoll.value < GRAVITY_WELL_ITEM_STEAL_CHANCE) {
          const itemRoll = mulberry32(state.rngState);
          state.rngState = itemRoll.nextState;
          const itemIndex = Math.min(
            Math.floor(itemRoll.value * victim.items.length),
            victim.items.length - 1,
          );
          const stolen = victim.items.splice(itemIndex, 1)[0];
          if (stolen != null) {
            stolenItemId = stolen;
            appendEvent(state, {
              type: 'item_steal_item',
              playerId: casterId,
              casterId,
              itemId: 'gravity_well',
              victimId,
              stolenItemId,
            });
            grantPlayerItem(state, casterId, stolen, 'item_theft');
          }
        }
      }

      // Emit item_pull (logical only; no position change)
      appendEvent(state, {
        type: 'item_pull',
        playerId: casterId,
        casterId,
        itemId: 'gravity_well',
        victimId,
        pulledFromNodeId: victim.nodeId,
        stoleItem: stolenItemId !== null,
      });
    }

    return { success: true, endsTurn: false };
  },
};

// Self-register at module load
addItem(gravityWell);
